import { z } from 'zod';
import type {
  CollectionDetail,
  CollectionItem,
  CollectionQuote,
  CollectionSummary,
} from '@sshop/domain';
import { parseDto } from '../../mappers/parseDto';
import type { Transport } from '../../transport/Transport';

const summaryDto = z.object({
  id: z.string(),
  slug: z.string(),
  title: z.string(),
  image_url: z.string().nullable(),
  share_image_url: z.string().nullable(),
  campaign_label: z.string().nullable(),
  savings_badge_format: z.enum(['amount', 'percent']),
  featured: z.boolean(),
  product_count: z.number(),
  price: z.number(),
  compare_at_price: z.number(),
  savings: z.number(),
  savings_percent: z.number(),
  available_quantity: z.number(),
});
const itemDto = z.object({
  id: z.string(),
  slug: z.string(),
  title: z.string(),
  sku: z.string().nullable(),
  image_url: z.string().nullable(),
  quantity: z.number(),
  regular_price: z.number(),
  selling_price: z.number(),
  available_quantity: z.number(),
  specifications: z.array(z.object({ name: z.string(), value: z.string() })),
});
const detailDto = summaryDto.extend({
  description: z.string(),
  short_description: z.string(),
  meta_title: z.string(),
  meta_description: z.string(),
  products: z.array(itemDto),
  quote: z.object({
    regular_total: z.number(),
    individual_total: z.number(),
    collection_price: z.number(),
    savings: z.number(),
    savings_percent: z.number(),
    available_quantity: z.number(),
    selected_products: z.array(z.number()),
  }),
  categories: z.array(z.object({ slug: z.string(), name: z.string() })),
  selection_mode: z.enum(['fixed', 'choice']),
  choice_groups: z.array(z.object({ name: z.string(), product_ids: z.array(z.number()) })),
  tier_two_percent: z.number(),
  tier_three_percent: z.number(),
});
const quoteDto = z.object({
  regular_total: z.number(),
  individual_total: z.number(),
  collection_price: z.number(),
  savings: z.number(),
  savings_percent: z.number(),
  available_quantity: z.number(),
  selected_products: z.array(z.number()),
});

function mapSummary(dto: z.infer<typeof summaryDto>): CollectionSummary {
  return {
    id: dto.id,
    slug: dto.slug,
    title: dto.title,
    imageUrl: dto.image_url,
    shareImageUrl: dto.share_image_url,
    campaignLabel: dto.campaign_label,
    savingsBadgeFormat: dto.savings_badge_format,
    featured: dto.featured,
    productCount: dto.product_count,
    price: dto.price,
    compareAtPrice: dto.compare_at_price,
    savings: dto.savings,
    savingsPercent: dto.savings_percent,
    availableQuantity: dto.available_quantity,
  };
}

function mapQuote(dto: z.infer<typeof quoteDto>): CollectionQuote {
  return {
    regularTotal: dto.regular_total,
    individualTotal: dto.individual_total,
    collectionPrice: dto.collection_price,
    savings: dto.savings,
    savingsPercent: dto.savings_percent,
    availableQuantity: dto.available_quantity,
    selectedProducts: dto.selected_products,
  };
}

function mapItem(dto: z.infer<typeof itemDto>): CollectionItem {
  return {
    id: dto.id,
    slug: dto.slug,
    title: dto.title,
    sku: dto.sku,
    imageUrl: dto.image_url,
    quantity: dto.quantity,
    regularPrice: dto.regular_price,
    sellingPrice: dto.selling_price,
    availableQuantity: dto.available_quantity,
    specifications: dto.specifications,
  };
}

function mapDetail(dto: z.infer<typeof detailDto>): CollectionDetail {
  return {
    ...mapSummary(dto),
    description: dto.description,
    shortDescription: dto.short_description,
    metaTitle: dto.meta_title,
    metaDescription: dto.meta_description,
    products: dto.products.map(mapItem),
    quote: mapQuote(dto.quote),
    categories: dto.categories,
    selectionMode: dto.selection_mode,
    choiceGroups: dto.choice_groups.map((group) => ({
      name: group.name,
      productIds: group.product_ids,
    })),
    tierTwoPercent: dto.tier_two_percent,
    tierThreePercent: dto.tier_three_percent,
  };
}

export interface CollectionService {
  list(categorySlug: string, signal?: AbortSignal): Promise<CollectionSummary[]>;
  featured(signal?: AbortSignal): Promise<CollectionSummary[]>;
  get(slug: string, signal?: AbortSignal): Promise<CollectionDetail>;
  track(slug: string, event: 'view' | 'add_to_cart'): Promise<void>;
  quote(slug: string, selectedProducts: number[], quantity?: number): Promise<CollectionQuote>;
}

export function createLaravelCollectionService(transport: Transport): CollectionService {
  return {
    async list(categorySlug, signal) {
      const response = await transport.request({
        method: 'GET',
        path: '/collections',
        query: { category: categorySlug },
        ...(signal ? { signal } : {}),
      });
      return parseDto(
        z.object({ data: z.array(summaryDto) }),
        response.data,
        'GET /collections',
      ).data.map(mapSummary);
    },
    async featured(signal) {
      const response = await transport.request({
        method: 'GET',
        path: '/collections',
        query: { featured: '1' },
        ...(signal ? { signal } : {}),
      });
      return parseDto(
        z.object({ data: z.array(summaryDto) }),
        response.data,
        'GET /collections?featured=1',
      ).data.map(mapSummary);
    },
    async get(slug, signal) {
      const response = await transport.request({
        method: 'GET',
        path: '/collections/' + encodeURIComponent(slug),
        ...(signal ? { signal } : {}),
      });
      return mapDetail(
        parseDto(z.object({ data: detailDto }), response.data, 'GET /collections/{slug}').data,
      );
    },
    async track(slug, event) {
      await transport.request({
        method: 'POST',
        path: '/collections/' + encodeURIComponent(slug) + '/events',
        body: { event },
      });
    },
    async quote(slug, selectedProducts, quantity = 1) {
      const response = await transport.request({
        method: 'POST',
        path: '/collections/' + encodeURIComponent(slug) + '/quote',
        body: { selected_products: selectedProducts, quantity },
      });
      return mapQuote(
        parseDto(z.object({ data: quoteDto }), response.data, 'POST /collections/{slug}/quote')
          .data,
      );
    },
  };
}
